"use client"

import { useMemo, useState } from "react"
import type { Event } from "openapi/api/model"
import { useGetEvents } from "openapi/api/endpoints"
import type { CalendarEventFormDataV2 } from "@/types/calendar"
import { CalendarEventDialog } from "@/components/calendar/calendar-event-dialog"
import { EventMeetingMetadata } from "@/components/calendar/event-meeting-metadata"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  QuietAction,
  SecondaryAction,
  TabHeader,
  TabSurface,
  TonePill,
} from "@/components/graphite/surface"
import { format, parseISO } from "date-fns"
import { CalendarIcon, Clock, Edit, FileText, MapPin, Plus, X } from "lucide-react"
import { createCalendarEventFormDefaults, mapEventToCalendarEventFormDefaults } from "@/lib/calendar-event-defaults"

interface CandidateEventsTabProps {
  candidateId: string
  candidateName: string
  onClose: () => void
}

interface JobCandidateEventsTabProps extends CandidateEventsTabProps {
  jobId: string
}

/**
 * Event types borrow the pipeline's hue vocabulary from canvas 4b rather than
 * inventing a second one, so a screening reads the same orange here as it does
 * on the board. The old blue-100/green-100 badges predate Graphite.
 */
const EVENT_HEX: Record<string, string> = {
  interview: "#2CC5B4",
  screening: "#FB8A3C",
  meeting: "#5B9BFA",
  other: "#7C8899",
}

function eventHex(type: string) {
  return EVENT_HEX[type] ?? EVENT_HEX.other
}

type CandidateEventsPanelProps = CandidateEventsTabProps & {
  defaultJobId?: string
}

function CandidateEventsPanel({ candidateId, candidateName, onClose, defaultJobId }: CandidateEventsPanelProps) {
  const { data: candidateEventsData } = useGetEvents({ candidate_id: candidateId }, { query: { enabled: !!candidateId } })

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create")
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)

  const candidateEvents = useMemo(() => {
    return (candidateEventsData?.data ?? [])
      .sort((a, b) => new Date(b.start_at ?? "").getTime() - new Date(a.start_at ?? "").getTime())
  }, [candidateEventsData?.data])

  const openCreateDialog = () => {
    setSelectedEvent(null)
    setDialogMode("create")
    setDialogOpen(true)
  }

  const openEditDialog = (event: Event) => {
    setSelectedEvent(event)
    setDialogMode("edit")
    setDialogOpen(true)
  }

  const dialogDefaults: Partial<CalendarEventFormDataV2> | undefined = selectedEvent
    ? mapEventToCalendarEventFormDefaults(selectedEvent, candidateId)
    : createCalendarEventFormDefaults({
        title: `${candidateName} - Interview`,
        linkType: "candidate",
        job_id: defaultJobId ?? "",
        candidate_id: candidateId,
      })

  return (
    <>
      <TabSurface>
        <TabHeader
          label="Events"
          count={candidateEvents.length}
          actions={
            <>
              <SecondaryAction onClick={openCreateDialog}>
                <Plus className="h-3.5 w-3.5" />
                New event
              </SecondaryAction>
              <QuietAction onClick={onClose} aria-label="Close events" className="px-2">
                <X className="h-3.5 w-3.5" />
              </QuietAction>
            </>
          }
        />

        {candidateEvents.length === 0 ? (
          <div className="flex flex-col items-start gap-2 pt-4">
            <p className="text-[12.5px] text-muted-foreground">
              No events yet. Schedule an interview, screening or meeting with{" "}
              {candidateName}.
            </p>
            <button
              type="button"
              onClick={openCreateDialog}
              className="text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
            >
              Create the first event
            </button>
          </div>
        ) : (
          <div className="pt-1">
            {candidateEvents.map((event) => {
              const startDate = event.start_at ? parseISO(event.start_at) : new Date()
              const endDate = event.end_at ? parseISO(event.end_at) : startDate
              const eventType = event.event_type ?? "other"
              const isPast = startDate < new Date()
              const hex = eventHex(eventType)

              return (
                <div
                  key={event.id}
                  className={cn("border-b py-3.5", isPast && "opacity-60")}
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-start gap-2.5">
                      <span
                        className="mt-[7px] h-[6px] w-[6px] flex-shrink-0 rounded-full"
                        style={{ backgroundColor: hex }}
                      />
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate text-[13px] font-medium">
                          {event.title}
                        </h4>
                        <span className="mt-1.5 block">
                          <TonePill hex={hex} dot={false}>
                            {eventType}
                          </TonePill>
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground"
                      onClick={() => openEditDialog(event)}
                    >
                      <Edit className="h-3 w-3" />
                      <span className="sr-only">Edit event</span>
                    </Button>
                  </div>

                  <div className="space-y-1.5 pl-[16px]">
                    {/* Dates and times are machine values, so they are mono. */}
                    <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                      <CalendarIcon className="h-3 w-3 flex-none" />
                      <span className="font-mono">
                        {format(startDate, "EEE d MMM yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                      <Clock className="h-3 w-3 flex-none" />
                      <span className="font-mono">
                        {format(startDate, "HH:mm")} – {format(endDate, "HH:mm")}
                      </span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-none" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    )}
                    {event.notes && (
                      <div className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
                        <FileText className="mt-0.5 h-3 w-3 flex-none" />
                        <span className="line-clamp-2">{event.notes}</span>
                      </div>
                    )}
                    <EventMeetingMetadata event={event} showAttendeeList />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </TabSurface>

      <CalendarEventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultValues={dialogDefaults}
        mode={dialogMode}
        selectedEvent={selectedEvent}
      />
    </>
  )
}

export function CandidateEventsTab(props: CandidateEventsTabProps) {
  return <CandidateEventsPanel {...props} />
}

export function JobCandidateEventsTab({ jobId, ...props }: JobCandidateEventsTabProps) {
  return <CandidateEventsPanel {...props} defaultJobId={jobId} />
}
